<script lang="ts">
import { T, useTask } from "@threlte/core";
import * as THREE from "three";
import { onMount, onDestroy } from "svelte";

interface Props {
  quantity?: number;
  /** Optional container element for contained mode — uses its dimensions instead of viewport */
  containerEl?: HTMLElement;
}

let { quantity = 150, containerEl }: Props = $props();

const utf8Decoder = new TextDecoder();
let wasmpath = "/wasm/cbps_we_core.wasm";

let canvasSize = { w: 1920, h: 1080 };
let mousePos = { x: 0, y: 0 };

let wasmInstance: WebAssembly.Instance | null = null;
let enginePtr: number = 0;
let particlesArrayPtr: number = 0;
let heapBase = 50000;

let isReady = $state(false);
let geometry = new THREE.BufferGeometry();
let particleBuffer: THREE.InterleavedBuffer;

const material = new THREE.ShaderMaterial({
  uniforms: {
    u_resolution: { value: new THREE.Vector2(canvasSize.w, canvasSize.h) },
    u_dpr: {
      value: typeof window !== "undefined" ? window.devicePixelRatio : 1,
    },
  },
  vertexShader: `
    in vec2 translate;
    in float radius;
    in vec4 color;
    uniform vec2 u_resolution;
    uniform float u_dpr;
    out vec4 vColor;
    void main() {
      vec2 finalPos = position.xy + translate;
      vec2 zeroToOne = finalPos / u_resolution;
      vec2 clipSpace = (zeroToOne * 2.0) - 1.0;
      gl_Position = vec4(clipSpace * vec2(1.0, -1.0), 0.0, 1.0);
      gl_PointSize = radius * u_dpr;
      vColor = color;
    }
  `,
  fragmentShader: `
    precision mediump float;
    in vec4 vColor;
    void main() {
      float dist = distance(gl_PointCoord, vec2(0.5)) * 2.0;
      float alpha = 1.0 - smoothstep(0.8, 1.0, dist);
      if (dist > 1.0) discard;
      pc_fragColor = vec4(vColor.rgb, vColor.a * alpha);
    }
  `,
  transparent: true,
  blending: THREE.NormalBlending,
  depthWrite: false,
});

function writeWasmString(str: string): number {
  if (!wasmInstance) return 0;
  const encoder = new TextEncoder();
  const buf = encoder.encode(str + "\0");
  const ptr = heapBase;
  heapBase = (heapBase + buf.length + 7) & ~7;
  const mem = new Uint8Array(
    (wasmInstance.exports.memory as WebAssembly.Memory).buffer,
  );
  mem.set(buf, ptr);
  return ptr;
}

function handleMouseMove(event: PointerEvent) {
  if (containerEl) {
    const rect = containerEl.getBoundingClientRect();
    mousePos.x = event.clientX - rect.left;
    mousePos.y = event.clientY - rect.top;
  } else {
    mousePos.x = event.clientX;
    mousePos.y = event.clientY;
  }
}

function handleDeviceOrientation(event: DeviceOrientationEvent) {
  const tiltX = Math.max(-45, Math.min(45, event.gamma || 0));
  const tiltY = Math.max(-45, Math.min(45, (event.beta || 0) - 45));
  mousePos.x = canvasSize.w / 2 + (tiltX / 45) * (canvasSize.w / 2);
  mousePos.y = canvasSize.h / 2 + (tiltY / 45) * (canvasSize.h / 2);
}

let lastTime = performance.now();

let resizeObserver: ResizeObserver | undefined;

onMount(() => {
  if (containerEl) {
    const rect = containerEl.getBoundingClientRect();
    canvasSize.w = rect.width;
    canvasSize.h = rect.height;

    resizeObserver = new ResizeObserver(() => {
      const r = containerEl!.getBoundingClientRect();
      canvasSize.w = r.width;
      canvasSize.h = r.height;
    });
    resizeObserver.observe(containerEl);
  } else {
    canvasSize.w = window.innerWidth;
    canvasSize.h = window.innerHeight;

    const onResize = () => {
      canvasSize.w = window.innerWidth;
      canvasSize.h = window.innerHeight;
    };
    window.addEventListener("resize", onResize);
  }

  const env = {
    js_write: (buf: number, n: number) => {
      const s = utf8Decoder.decode(
        new Uint8Array(
          (wasmInstance!.exports.memory as WebAssembly.Memory).buffer,
          buf,
          n,
        ),
      );
      console.log("[particle-wasm]", s);
    },
  };

  fetch(wasmpath)
    .then((res) => {
      if (!res.ok) throw new Error(`WASM fetch failed: ${res.status}`);
      return res.arrayBuffer();
    })
    .then((bytes) => WebAssembly.instantiate(bytes, { env }))
    .then(({ instance }) => {
      wasmInstance = instance;
      heapBase = (instance.exports.__heap_base as WebAssembly.Global).value;

      const randomSeed = Math.floor(Date.now()) >>> 0;
      (instance.exports.cbps_engine_set_seed as Function)(randomSeed);

      const h1Ptr = writeWasmString("");
      const h2Ptr = writeWasmString("");

      enginePtr = (instance.exports.cbps_engine_create as Function)(
        canvasSize.w,
        canvasSize.h,
        quantity,
        h1Ptr,
        h2Ptr,
      );

      const memView = new DataView(
        (instance.exports.memory as WebAssembly.Memory).buffer,
      );
      particlesArrayPtr = memView.getUint32(enginePtr + 0, true);
      const arrayBuffer = (instance.exports.memory as WebAssembly.Memory)
        .buffer;

      particleBuffer = new THREE.InterleavedBuffer(
        new Float32Array(arrayBuffer, particlesArrayPtr, quantity * 12),
        12,
      );
      geometry.setAttribute(
        "position",
        new THREE.InterleavedBufferAttribute(particleBuffer, 2, 0),
      );
      geometry.setAttribute(
        "translate",
        new THREE.InterleavedBufferAttribute(particleBuffer, 2, 2),
      );
      geometry.setAttribute(
        "radius",
        new THREE.InterleavedBufferAttribute(particleBuffer, 1, 7),
      );
      geometry.setAttribute(
        "color",
        new THREE.InterleavedBufferAttribute(particleBuffer, 4, 8),
      );

      lastTime = performance.now();
      isReady = true;
      console.log(
        "[particles] WASM ready, enginePtr:",
        enginePtr,
        "particlesArrayPtr:",
        particlesArrayPtr,
        "quantity:",
        quantity,
      );
    })
    .catch((err) => {
      console.error("[particles] Failed to load WASM:", err);
    });
});

onDestroy(() => {
  if (enginePtr && wasmInstance) {
    (wasmInstance.exports.cbps_engine_destroy as Function)(enginePtr);
  }
  resizeObserver?.disconnect();
});

useTask(() => {
  if (!isReady || !wasmInstance) return;

  material.uniforms.u_resolution.value.set(canvasSize.w, canvasSize.h);

  const currentTime = performance.now();
  const delta = Math.min((currentTime - lastTime) / 1000, 0.05);
  lastTime = currentTime;

  (wasmInstance.exports.cbps_engine_update as Function)(
    enginePtr,
    delta,
    0,
    0,
    canvasSize.w,
    canvasSize.h,
    Math.round(mousePos.x),
    Math.round(mousePos.y),
  );

  const mem = new Float32Array(
    (wasmInstance.exports.memory as WebAssembly.Memory).buffer,
  );
  const base = particlesArrayPtr / 4;
  /*console.log("[particles] frame:", {
    px: mem[base],
    py: mem[base + 1],
    tx: mem[base + 2],
    ty: mem[base + 3],
    r: mem[base + 7],
    cr: mem[base + 8],
    cg: mem[base + 9],
    cb: mem[base + 10],
    resolution: `${canvasSize.w}x${canvasSize.h}`,
  });*/

  particleBuffer.needsUpdate = true;
});
</script>

<svelte:window
  onpointermove={handleMouseMove}
  ondeviceorientation={handleDeviceOrientation}
/>

{#if isReady}
  <T.Points {geometry} {material} frustumCulled={false} />
{/if}
